import React, { useState, useEffect, useCallback } from 'react';
import { signatureAPI } from '../../services/api';
import { Button, Modal, ConfirmDialog } from '../../components/common';
import { toast } from 'react-toastify';

const statusLabels = {
  pending:   { label: 'En attente',  color: '#f59e0b', bg: 'rgba(245,158,11,.1)' },
  signed:    { label: 'Signé',       color: '#10b981', bg: 'rgba(16,185,129,.1)' },
  expired:   { label: 'Expiré',      color: '#94a3b8', bg: 'rgba(148,163,184,.1)' },
  cancelled: { label: 'Annulé',      color: '#ef4444', bg: 'rgba(239,68,68,.1)' },
};

export const AdminSignaturesPage = () => {
  const [signatures, setSignatures] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [proofModal, setProofModal] = useState(false);
  const [proof,      setProof]      = useState(null);
  const [loadingProof, setLoadingProof] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    signatureAPI.list()
      .then(({ data }) => setSignatures(data.data || []))
      .catch(() => toast.error('Erreur chargement signatures'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteSig = async (id) => {
    try {
      await signatureAPI.delete(id);
      toast.success('Signature supprimée');
      setConfirmDel(null);
      load();
    } catch { toast.error('Erreur suppression'); }
  };

  const openProof = async (sig) => {
    setProofModal(true);
    setLoadingProof(true);
    setProof(null);
    try {
      const { data } = await signatureAPI.getProof(sig.id);
      setProof(data.data);
    } catch {
      toast.error('Erreur chargement preuve');
      setProofModal(false);
    } finally {
      setLoadingProof(false);
    }
  };

  const downloadProofPdf = () => {
    if (!proof) return;
    // Générer un résumé textuel de la preuve à copier/imprimer
    const text = `PREUVE DE SIGNATURE ELECTRONIQUE
======================================
Devis : ${proof.quote_ref || 'N/A'}
Montant TTC : ${Number(proof.total_ttc || 0).toFixed(2)} EUR

Client : ${proof.client_name}
Email : ${proof.client_email}

Date de signature : ${proof.signed_at || 'Non signé'}
Adresse IP : ${proof.signer_ip || 'N/A'}
User-Agent : ${proof.signer_ua || 'N/A'}
Hash de preuve : ${proof.proof_hash || 'N/A'}
Statut : ${proof.status}

--- Articles du devis ---
${(proof.items || []).map((it, i) => `${i + 1}. ${it.description || it.name || 'Article'} — ${it.quantity || 1} x ${Number(it.unit_price || 0).toFixed(2)} EUR`).join('\n')}

--- Données client ---
${Object.entries(proof.client || {}).map(([k, v]) => `${k}: ${v}`).join('\n')}
======================================
Ce document constitue une preuve de signature electronique.
`;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `preuve-signature-${proof.quote_ref || proof.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Signatures electroniques</h1>
          <p className="admin-page-subtitle">{signatures.length} signature(s) enregistree(s)</p>
        </div>
        <Button variant="secondary" onClick={load}>Actualiser</Button>
      </div>

      <div className="admin-page-content">
        <div style={card}>
          {loading ? (
            <div className="page-loader"><div className="spinner" /></div>
          ) : !signatures.length ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✍️</div>
              <div style={{ fontWeight: 700 }}>Aucune signature enregistree</div>
              <p style={{ fontSize: 13, marginTop: 8 }}>Les signatures apparaitront ici lorsque des devis seront envoyes a la signature.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Devis</th>
                  <th>Client</th>
                  <th>Email</th>
                  <th>Statut</th>
                  <th>Date signature</th>
                  <th>IP</th>
                  <th>Hash preuve</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {signatures.map(sig => {
                  const st = statusLabels[sig.status] || statusLabels.pending;
                  return (
                    <tr key={sig.id}>
                      <td style={{ fontWeight: 700 }}>{sig.quote_ref || `#${sig.quote_id}`}</td>
                      <td>{sig.client_name}</td>
                      <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{sig.client_email}</td>
                      <td>
                        <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: st.bg, color: st.color }}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {sig.signed_at ? new Date(sig.signed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{sig.signer_ip || '—'}</td>
                      <td style={{ fontSize: 11, fontFamily: 'monospace', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }} title={sig.proof_hash}>
                        {sig.proof_hash ? sig.proof_hash.slice(0, 16) + '...' : '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button size="sm" variant="secondary" onClick={() => openProof(sig)}>Consulter</Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmDel(sig.id)} title="Supprimer">🗑</Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal preuve detaillee */}
      <Modal isOpen={proofModal} onClose={() => setProofModal(false)} title="Preuve de signature electronique" size="xl"
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setProofModal(false)}>Fermer</Button>
            {proof && proof.status === 'signed' && (
              <Button variant="primary" onClick={downloadProofPdf}>Telecharger la preuve</Button>
            )}
          </div>
        }
      >
        {loadingProof ? (
          <div className="page-loader"><div className="spinner" /></div>
        ) : proof ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* En-tete */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ padding: 14, background: 'var(--bg-page)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Devis</div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>{proof.quote_ref || `#${proof.quote_id}`}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                  Montant TTC : <strong>{Number(proof.total_ttc || 0).toFixed(2)} EUR</strong>
                </div>
              </div>
              <div style={{ padding: 14, background: 'var(--bg-page)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Client</div>
                <div style={{ fontWeight: 700 }}>{proof.client_name}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{proof.client_email}</div>
              </div>
            </div>

            {/* Infos signature */}
            <div style={{ padding: 14, background: proof.status === 'signed' ? 'rgba(16,185,129,.05)' : 'rgba(245,158,11,.05)', borderRadius: 8, border: `1px solid ${proof.status === 'signed' ? 'rgba(16,185,129,.2)' : 'rgba(245,158,11,.2)'}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Informations de signature</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                <div><strong>Statut :</strong> {(statusLabels[proof.status] || {}).label || proof.status}</div>
                <div><strong>Date :</strong> {proof.signed_at ? new Date(proof.signed_at).toLocaleString('fr-FR') : 'Non signe'}</div>
                <div><strong>Adresse IP :</strong> {proof.signer_ip || '—'}</div>
                <div><strong>Navigateur :</strong> <span style={{ fontSize: 11, wordBreak: 'break-all' }}>{proof.signer_ua || '—'}</span></div>
              </div>
            </div>

            {/* Hash de preuve */}
            {proof.proof_hash && (
              <div style={{ padding: 14, background: 'rgba(59,130,246,.05)', borderRadius: 8, border: '1px solid rgba(59,130,246,.15)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Hash de preuve cryptographique (SHA-256)</div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: '#3b82f6', cursor: 'pointer' }}
                  onClick={() => { navigator.clipboard.writeText(proof.proof_hash); toast.success('Hash copie'); }}
                  title="Cliquer pour copier">
                  {proof.proof_hash}
                </div>
              </div>
            )}

            {/* Signature visuelle */}
            {proof.signature_data && proof.signature_data.startsWith('data:image/') && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Signature visuelle</div>
                <div style={{ background: 'white', borderRadius: 8, border: '1px solid var(--border)', padding: 16, textAlign: 'center' }}>
                  <img src={proof.signature_data} alt="Signature" style={{ maxWidth: '100%', maxHeight: 200 }} />
                </div>
              </div>
            )}

            {/* Articles du devis */}
            {proof.items && proof.items.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Articles du devis</div>
                <table className="data-table" style={{ fontSize: 12 }}>
                  <thead><tr><th>Description</th><th>Qte</th><th>PU HT</th><th>Total HT</th></tr></thead>
                  <tbody>
                    {proof.items.map((it, i) => (
                      <tr key={i}>
                        <td>{it.description || it.name || 'Article'}</td>
                        <td style={{ textAlign: 'center' }}>{it.quantity || 1}</td>
                        <td style={{ textAlign: 'right' }}>{Number(it.unit_price || 0).toFixed(2)} EUR</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{(Number(it.quantity || 1) * Number(it.unit_price || 0)).toFixed(2)} EUR</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={() => deleteSig(confirmDel)}
        title="Supprimer la signature"
        message="Cette signature sera définitivement supprimée. Cette action est irréversible."
        confirmLabel="Supprimer"
        danger
      />
    </div>
  );
};

export default AdminSignaturesPage;
